import { ProductionRun } from 'types/production';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import { getCopy } from 'ui-component/ArchiveDialog/copy';
import { ManagementActionTypes } from 'types';
import useHandleArchive from 'ui-component/ArchiveDialog/useHandleArchive';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { BomCRUDSchema } from 'types/bom';
import { FC, useEffect } from 'react';
import { DialogContentText } from '@mui/material';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';

interface ArchiveDialogProps {
  idToUpdate: BomCRUDSchema['id'] | ProductionRun['id'];
  archived: boolean;
  dialogOpen: boolean;
  onClose: () => void;
  actionType: Exclude<
    keyof typeof ManagementActionTypes,
    'supplier' | 'purchaseRule'
  >;
  message?: string;
}

const ArchiveDialog: FC<ArchiveDialogProps> = ({
  idToUpdate,
  archived,
  dialogOpen,
  onClose,
  actionType,
  message,
}) => {
  const copy = getCopy(archived);

  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const { handleArchive, isLoading, isSuccess, error, resetError } =
    useHandleArchive({ id: idToUpdate, actionType, archived });

  const handleClose = () => {
    resetError();
    onClose();
  };

  useEffect(() => {
    if (isSuccess && !isLoading) {
      handleClose();
    }
  }, [isSuccess, isLoading]);

  const appIds = {
    [ManagementActionTypes.bom]: ALL_APPS.BOMS.id,
    [ManagementActionTypes.productionRun]: ALL_APPS.PRODUCTION.id,
    [ManagementActionTypes.purchaseOrder]: ALL_APPS.PROCUREMENT.id,
    [ManagementActionTypes.team]: ALL_APPS.PROPERTIES.id,
  };

  return (
    <ExtendedDialog
      title={handleReplaceWithCustomTeamName(copy[actionType].title)}
      open={dialogOpen}
      onCloseDialog={handleClose}
      submitButtonCopy={handleReplaceWithCustomTeamName(
        copy[actionType]?.submitButton
      )}
      onClickPrimaryButton={handleArchive}
      formSubmitError={error}
      isSubmitting={isLoading}
      permissionScope={{
        app: appIds[actionType],
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      <DialogContentText>
        {handleReplaceWithCustomTeamName(copy[actionType].body)}
        {message}
      </DialogContentText>
    </ExtendedDialog>
  );
};

export default ArchiveDialog;
